/* BENUTE — dialogs, authoritative cart state and measurement guide. */
(() => {
  'use strict';
  if (window.BenuteStudio) return;
  const $ = (s, root = document) => root.querySelector(s);
  const $$ = (s, root = document) => [...root.querySelectorAll(s)];
  const openers = new WeakMap();
  function open(dialog, opener) {
    if (!dialog?.showModal) return false;
    if (!dialog.open) {
      openers.set(dialog, opener || document.activeElement);
      dialog.showModal();
    }
    document.documentElement.classList.add('bn-modal-open');
    return true;
  }
  function close(dialog) { if (dialog?.open) dialog.close(); }
  function bindDialogs(scope = document) {
    $$('dialog.bn-modal, dialog.bn-drawer', scope).forEach(dialog => {
      if (dialog.dataset.bnBound) return;
      dialog.dataset.bnBound = 'true';
      dialog.addEventListener('click', event => {
        if (event.target.closest('[data-bn-close-dialog]')) close(dialog);
        else if (event.target === dialog) {
          const r = dialog.getBoundingClientRect();
          if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) close(dialog);
        }
      });
      dialog.addEventListener('close', () => {
        if ($('dialog[open]')) return;
        document.documentElement.classList.remove('bn-modal-open');
        const opener = openers.get(dialog);
        if (opener?.isConnected) opener.focus?.({ preventScroll: true });
      });
    });
  }
  const cart = $('[data-bn-cart]');
  const rootPath = window.Shopify?.routes?.root || cart?.dataset.bnRoot || '/';
  const base = rootPath.endsWith('/') ? rootPath : rootPath + '/';
  const cartURL = cart?.dataset.bnCartUrl || base + 'cart';
  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
    style: 'currency', currency: currency || window.Shopify?.currency?.active || 'BRL'
  }).format(Number(cents || 0) / 100);
  const element = (tag, className, value) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value !== undefined) node.textContent = value;
    return node;
  };
  const safeURL = (value, fallback = '#') => {
    try { const url = new URL(value, location.origin); return /^https?:$/.test(url.protocol) ? url.href : fallback; }
    catch (_) { return fallback; }
  };
  async function request(url, options = {}) {
    const controller = new AbortController();
    const external = options.signal;
    const abort = () => controller.abort();
    if (external?.aborted) abort();
    external?.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, 15000);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal,
        headers: { Accept: 'application/json', ...options.headers }
      });
      let data;
      try { data = await response.json(); }
      catch (_) { throw new Error('Não foi possível confirmar a resposta. Confira a sacola antes de tentar novamente.'); }
      if (!response.ok) {
        const message = typeof data?.description === 'string' ? data.description : typeof data?.message === 'string' ? data.message : 'Não foi possível concluir. Confira os dados e tente novamente.';
        throw new Error(message);
      }
      return data;
    } finally { clearTimeout(timeout); external?.removeEventListener('abort', abort); }
  }
  window.BenuteStudio = { open, close, bindDialogs, money, element, safeURL, request, base };

  let snapshot = null, generation = 0, pending = 0, fresh = false;
  let mutationTail = Promise.resolve(), readPromise = null, readController = null;
  try { snapshot = JSON.parse($('[data-bn-cart-initial]', cart)?.textContent || 'null'); } catch (_) { /* Server HTML remains available. */ }
  const validCart = data => data && Array.isArray(data.items) && Number.isFinite(data.item_count) && Number.isFinite(data.total_price);
  const signature = data => JSON.stringify([data?.item_count, data?.total_price, data?.items?.map(item => [item.key, item.quantity, item.final_line_price])]);
  function status(message, retry = false) {
    if (!cart) return;
    $('[data-bn-cart-status]', cart).textContent = message;
    $('[data-bn-cart-retry]', cart).hidden = !retry;
  }
  function controls() {
    if (!cart) return;
    cart.setAttribute('aria-busy', String(pending > 0 || Boolean(readPromise)));
    $$('[data-bn-cart-quantity], button[name="checkout"]', cart).forEach(button => button.disabled = pending > 0 || !fresh);
  }
  function quantityButton(item, next, text, action) {
    const button = element('button', '', text);
    button.type = 'button'; button.dataset.bnCartQuantity = next; button.dataset.bnQuantityAction = action;
    button.setAttribute('aria-label', `${action === 'remove' ? 'Remover' : action === 'decrease' ? 'Diminuir quantidade de' : 'Aumentar quantidade de'} ${item.product_title || item.title}`);
    return button;
  }
  function renderCart(data) {
    if (!validCart(data)) throw new Error('Não foi possível atualizar sua sacola. Tente carregar novamente.');
    const changed = signature(snapshot) !== signature(data);
    snapshot = data; fresh = true;
    $$('.header__cart-count').forEach(node => node.textContent = data.item_count);
    if (!cart) return data;
    $('[data-bn-cart-count]', cart).textContent = `(${data.item_count})`;
    const state = element('div'); state.dataset.bnCartState = ''; state.dataset.count = data.item_count;
    if (!data.items.length) {
      const empty = element('div', 'bn-cart-empty');
      empty.append(element('p', 'bn-eyebrow', 'O COMEÇO DE UM NOVO LOOK'), element('h3', '', 'Sua sacola está vazia.'), element('p', '', 'Encontre as peças que combinam com você.'));
      const link = element('a', 'bn-button bn-button--dark', 'Explorar a coleção →'); link.href = cart.dataset.bnCollectionUrl || base + 'collections/all';
      empty.append(link); state.append(empty);
    } else {
      const lines = element('div', 'bn-cart-lines');
      data.items.forEach(item => {
        const line = element('article', 'bn-cart-line'); line.dataset.bnLineKey = item.key;
        const imageLink = element('a', 'bn-cart-line-image'); imageLink.href = safeURL(item.url, cartURL); imageLink.tabIndex = -1; imageLink.setAttribute('aria-hidden', 'true');
        const src = item.featured_image?.url || item.image;
        if (src) { const img = element('img'); img.src = safeURL(src, ''); img.alt = ''; img.width = 110; img.height = 150; imageLink.append(img); }
        const info = element('div', 'bn-cart-line-info');
        const title = element('a', '', item.product_title || item.title); title.href = safeURL(item.url, cartURL); info.append(title);
        if (item.variant_title && item.variant_title !== 'Default Title') info.append(element('p', '', item.variant_title));
        if (item.selling_plan_allocation?.selling_plan?.name) info.append(element('p', '', item.selling_plan_allocation.selling_plan.name));
        Object.entries(item.properties || {}).forEach(([key, value]) => { if (value && !key.startsWith('_')) info.append(element('p', '', `${key}: ${value}`)); });
        const bottom = element('div', 'bn-cart-line-bottom');
        const quantity = element('div', 'bn-cart-quantity');
        quantity.append(quantityButton(item, Math.max(0, item.quantity - 1), '−', 'decrease'), element('span', '', item.quantity), quantityButton(item, item.quantity + 1, '+', 'increase'));
        bottom.append(quantity, element('strong', '', money(item.final_line_price ?? item.line_price, data.currency))); info.append(bottom);
        if (item.original_line_price > item.final_line_price) info.append(element('s', '', money(item.original_line_price, data.currency)));
        (item.line_level_discount_allocations || []).forEach(discount => info.append(element('p', 'bn-cart-discount', `${discount.discount_application.title} (−${money(discount.amount, data.currency)})`)));
        const remove = quantityButton(item, 0, 'Remover', 'remove'); remove.className = 'bn-cart-remove'; info.append(remove);
        line.append(imageLink, info); lines.append(line);
      });
      const summary = element('div', 'bn-cart-summary');
      (data.cart_level_discount_applications || []).forEach(discount => {
        const row = element('p', 'bn-cart-total'); row.append(element('span', '', discount.title), element('span', '', `−${money(discount.total_allocated_amount, data.currency)}`)); summary.append(row);
      });
      const total = element('p', 'bn-cart-total'); total.append(element('strong', '', 'Subtotal'), element('strong', '', money(data.total_price, data.currency)));
      const checkout = element('form'); checkout.action = cartURL; checkout.method = 'post';
      const pay = element('button', 'bn-button bn-button--dark bn-button--full', 'Finalizar compra →'); pay.type = 'submit'; pay.name = 'checkout'; checkout.append(pay);
      const keep = element('button', 'bn-button bn-button--full', 'Continuar comprando'); keep.type = 'button'; keep.dataset.bnCloseDialog = '';
      summary.append(total, element('p', '', 'Frete e eventuais descontos calculados na finalização.'), checkout, keep);
      state.append(lines, summary);
    }
    $('[data-bn-cart-content]', cart).replaceChildren(state); controls();
    if (changed) document.dispatchEvent(new CustomEvent('benute:cart-updated', { detail: { cart: data } }));
    return data;
  }
  function invalidate() {
    generation++; fresh = false;
    readController?.abort(); readController = null; readPromise = null;
    controls();
  }
  function readCart() {
    if (readPromise) return readPromise;
    const current = generation;
    const controller = new AbortController(); readController = controller;
    const url = new URL(base + 'cart.js', location.origin); url.searchParams.set('_bn', `${Date.now()}-${current}`);
    const operation = request(url, { signal: controller.signal }).then(data => {
      if (current !== generation) throw new DOMException('Outdated cart response', 'AbortError');
      return renderCart(data);
    }).finally(() => {
      if (readPromise === operation) { readPromise = null; readController = null; controls(); }
    });
    readPromise = operation; controls(); return operation;
  }
  async function refreshCart() {
    // Reads wait for every queued write; an older response can never replace a newer cart.
    for (;;) {
      const tail = mutationTail;
      await tail.catch(() => {});
      if (tail !== mutationTail) continue;
      const current = generation;
      try { return await readCart(); }
      catch (error) { if (current !== generation) continue; throw error; }
    }
  }
  function enqueue(action) {
    pending++; controls();
    const operation = mutationTail.catch(() => {}).then(async () => {
      invalidate();
      try { return await action(); }
      finally { pending--; controls(); }
    });
    mutationTail = operation;
    return operation;
  }
  async function openCart(opener) {
    if (!open(cart, opener)) { location.assign(cartURL); return; }
    status(pending ? 'Atualizando sua sacola…' : 'Conferindo suas escolhas…');
    try { await refreshCart(); status(''); }
    catch (_) { status('Não foi possível atualizar. Tente carregar novamente ou abra a página da sacola.', true); }
  }
  function addToCart(data, opener, options = {}) {
    return enqueue(async () => {
      let result;
      try {
        const isForm = data instanceof FormData;
        if (isForm) { data.delete('sections'); data.delete('sections_url'); }
        result = await request(base + 'cart/add.js', { method: 'POST', headers: isForm ? {} : { 'Content-Type': 'application/json' }, body: isForm ? data : JSON.stringify(data) });
      } catch (error) {
        // Never retry a write automatically: a lost response may follow a successful add.
        try { await readCart(); } catch (_) { /* Keep the last visible cart and recovery link. */ }
        const message = error.name === 'AbortError' || error instanceof TypeError ? 'Não foi possível confirmar o envio. Confira a sacola antes de adicionar novamente.' : error.message;
        status(message, !fresh); throw new Error(message);
      }
      if (options.open !== false) { close($('#BN-Quick')); if (!open(cart, opener)) { location.assign(cartURL); return result; } }
      status('Peça adicionada. Atualizando sua sacola…');
      try { await readCart(); status('Peça adicionada à sacola.'); }
      catch (_) { status('Peça adicionada. Carregue novamente para conferir os itens.', true); }
      return result;
    });
  }
  function changeQuantity(key, quantity) {
    if (!Number.isInteger(quantity) || quantity < 0) return Promise.reject(new Error('Quantidade inválida.'));
    return enqueue(async () => {
      status('Atualizando sua sacola…');
      try {
        const data = await request(base + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
        if (validCart(data)) renderCart(data); else await readCart();
        status('Sacola atualizada.'); return snapshot;
      } catch (error) {
        try { await readCart(); } catch (_) { /* Retain existing items and expose retry. */ }
        status(error.name === 'AbortError' || error instanceof TypeError ? 'Confira a quantidade antes de tentar novamente.' : error.message, !fresh);
        throw error;
      }
    });
  }
  window.BenuteCart = { open: openCart, add: addToCart, refresh: refreshCart, change: changeQuantity, get state() { return snapshot; } };
  if (cart) {
    cart.addEventListener('click', async event => {
      if (event.target.closest('[data-bn-cart-retry]')) { openCart(); return; }
      const button = event.target.closest('[data-bn-cart-quantity]');
      if (!button || pending || !fresh) return;
      const line = button.closest('[data-bn-line-key]'); if (!line) return;
      const key = line.dataset.bnLineKey, action = button.dataset.bnQuantityAction;
      try { await changeQuantity(key, Number(button.dataset.bnCartQuantity)); } catch (_) { /* Message is already shown in the drawer. */ }
      const updated = $$('[data-bn-line-key]', cart).find(node => node.dataset.bnLineKey === key);
      const target = updated && $$('[data-bn-quantity-action]', updated).find(node => node.dataset.bnQuantityAction === action);
      if (cart.open) (target || $('[data-bn-close-dialog]', cart))?.focus({ preventScroll: true });
    });
    document.addEventListener('cart:refresh', event => {
      if (window.theme?.pageType === 'cart') return;
      event.stopImmediatePropagation();
      refreshCart().catch(() => { if (cart.open) status('Não foi possível atualizar a sacola.', true); });
    }, true);
    document.addEventListener('product:added', event => { event.stopImmediatePropagation(); openCart(event.target); }, true);
    window.addEventListener('pageshow', event => { if (event.persisted) { invalidate(); refreshCart().catch(() => { if (cart.open) status('Carregue sua sacola novamente.', true); }); } });
    document.addEventListener('visibilitychange', () => { if (!document.hidden && cart.open) openCart(); });
  }

  function showMeasureTab(dialog, tab) {
    $$('[data-bn-measure-tab]', dialog).forEach(button => button.setAttribute('aria-pressed', String(button.dataset.bnMeasureTab === tab)));
    $$('[data-bn-measure-panel]', dialog).forEach(panel => panel.hidden = panel.dataset.bnMeasurePanel !== tab);
  }
  function calculateSize(dialog) {
    const result = $('[data-bn-measure-result]', dialog), field = $('[data-bn-measure-input]', dialog);
    if (!field || !result) return;
    const value = Number(field.value);
    if (!field.value || !field.reportValidity() || !Number.isFinite(value) || value <= 0) { result.textContent = 'Informe uma medida válida em centímetros.'; return; }
    let config;
    try { config = JSON.parse($('[data-bn-size-data]', dialog).textContent); } catch (_) { result.textContent = 'Consulte a tabela de medidas.'; return; }
    const rows = (config?.linhas || []).map(row => ({ size: row.tamanho, value: Number(row[config.medida]) })).filter(row => row.size && Number.isFinite(row.value) && row.value > 0).sort((a, b) => a.value - b.value);
    if (!rows.length || value < rows[0].value - 2 || value > rows[rows.length - 1].value + 2) { result.textContent = 'Sua medida está fora da faixa desta tabela. Confira o guia completo antes de escolher.'; return; }
    const closest = rows.reduce((best, row) => Math.abs(row.value - value) < Math.abs(best.value - value) ? row : best);
    result.textContent = `A medida mais próxima é a do tamanho ${closest.size}: ${closest.value} cm. Compare também comprimento e demais medidas antes de escolher. A disponibilidade aparece no seletor do produto.`;
  }
  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-bn-dialog-open]');
    if (opener) { event.preventDefault(); const dialog = document.getElementById(opener.dataset.bnDialogOpen); if (dialog) { showMeasureTab(dialog, opener.dataset.bnSizeTab || 'table'); open(dialog, opener); } }
    const tab = event.target.closest('[data-bn-measure-tab]'); if (tab) showMeasureTab(tab.closest('dialog'), tab.dataset.bnMeasureTab);
    const calculate = event.target.closest('[data-bn-measure-calculate]'); if (calculate) calculateSize(calculate.closest('dialog'));
    const cartOpener = event.target.closest('[data-bn-open-cart]'); if (cartOpener && cart) { event.preventDefault(); openCart(cartOpener); }
  });
  function init(scope = document) {
    bindDialogs(scope);
    $$('[data-bn-edits]', scope).forEach(section => {
      if (section.dataset.bnReady) return;
      section.dataset.bnReady = 'true';
      const tabs = $$('[data-bn-edit-tab]', section);
      const select = tab => { tabs.forEach(button => button.setAttribute('aria-pressed', String(button === tab))); $$('[data-bn-edit-panel]', section).forEach(panel => panel.hidden = panel.id !== tab.dataset.bnEditTab); };
      tabs.forEach(tab => tab.addEventListener('click', () => select(tab))); if (tabs[0]) select(tabs[0]);
    });
    $$('.bn-grid', scope).forEach(grid => { grid.dataset.bnItems = grid.children.length; });
  }
  init(); document.addEventListener('shopify:section:load', event => init(event.target));
})();
